"use client"

import React, { useState, useEffect, useCallback, useMemo } from "react"
import { Clock, MapPin, Settings2 } from "lucide-react"
import { Button } from "../ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip"
import {
  TimezoneMapDialog,
  getStoredClockLocations,
  setStoredClockLocations,
  type ClockLocation,
} from "./timezone-map-dialog"
import {
  CurrencySelectDialog,
  getStoredCurrencyPairs,
  setStoredCurrencyPairs,
  type CurrencyPair,
} from "./currency-select-dialog"
import { cn } from "../lib/utils"
import { useSidebarUser, updateUserPreferences } from "../lib/auth"

const DEFAULT_API_BASE = typeof process !== "undefined" ? process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000" : "http://localhost:8000"

function formatTime(timezone: string, lon?: number): string {
  try {
    if (timezone === "UTC" && lon != null && !Number.isNaN(lon)) {
      const offsetHours = lon / 15
      const offsetMs = offsetHours * 60 * 60 * 1000
      return new Date(Date.now() + offsetMs).toLocaleTimeString(undefined, {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
    }
    return new Date().toLocaleTimeString(undefined, {
      timeZone: timezone,
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
  } catch {
    return "--:--"
  }
}

function formatTimezoneShort(timezone: string, lon?: number): string {
  if (timezone === "UTC" && lon != null && !Number.isNaN(lon)) {
    const offsetHours = lon / 15
    const sign = offsetHours >= 0 ? "+" : ""
    const h = Math.floor(Math.abs(offsetHours))
    const m = Math.round((Math.abs(offsetHours) - h) * 60)
    const pad = (n: number) => n.toString().padStart(2, "0")
    return `UTC${sign}${h}${m ? ":" + pad(m) : ""}`
  }
  try {
    const formatter = new Intl.DateTimeFormat(undefined, { timeZone: timezone, timeZoneName: "short" })
    const parts = formatter.formatToParts(new Date())
    const tzPart = parts.find((p) => p.type === "timeZoneName")
    return tzPart?.value ?? timezone.split("/").pop() ?? timezone
  } catch {
    return timezone.split("/").pop() ?? timezone
  }
}

function getLocalTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return "UTC"
  }
}

const TZ_TO_LOCATION: Record<string, string> = {
  "Europe/London": "London, United Kingdom",
  "Europe/Berlin": "Berlin, Germany",
  "Europe/Paris": "Paris, France",
  "Europe/Amsterdam": "Amsterdam, Netherlands",
  "Europe/Brussels": "Brussels, Belgium",
  "Europe/Zurich": "Zurich, Switzerland",
  "Europe/Vienna": "Vienna, Austria",
  "Europe/Madrid": "Madrid, Spain",
  "Europe/Rome": "Rome, Italy",
  "Europe/Stockholm": "Stockholm, Sweden",
  "Europe/Oslo": "Oslo, Norway",
  "Europe/Copenhagen": "Copenhagen, Denmark",
  "Europe/Helsinki": "Helsinki, Finland",
  "Europe/Dublin": "Dublin, Ireland",
  "Europe/Warsaw": "Warsaw, Poland",
  "Europe/Prague": "Prague, Czech Republic",
  "Europe/Budapest": "Budapest, Hungary",
  "Europe/Bucharest": "Bucharest, Romania",
  "Europe/Athens": "Athens, Greece",
  "Europe/Istanbul": "Istanbul, Turkey",
  "Europe/Moscow": "Moscow, Russia",
  "Asia/Dubai": "Dubai, United Arab Emirates",
  "Asia/Kolkata": "New Delhi, India",
  "Asia/Karachi": "Karachi, Pakistan",
  "Asia/Dhaka": "Dhaka, Bangladesh",
  "Asia/Bangkok": "Bangkok, Thailand",
  "Asia/Singapore": "Singapore, Singapore",
  "Asia/Hong_Kong": "Hong Kong, Hong Kong",
  "Asia/Shanghai": "Shanghai, China",
  "Asia/Tokyo": "Tokyo, Japan",
  "Asia/Seoul": "Seoul, South Korea",
  "Australia/Sydney": "Sydney, Australia",
  "Australia/Melbourne": "Melbourne, Australia",
  "Australia/Perth": "Perth, Australia",
  "Pacific/Auckland": "Auckland, New Zealand",
  "America/New_York": "New York, United States",
  "America/Chicago": "Chicago, United States",
  "America/Denver": "Denver, United States",
  "America/Los_Angeles": "Los Angeles, United States",
  "America/Phoenix": "Phoenix, United States",
  "America/Toronto": "Toronto, Canada",
  "America/Vancouver": "Vancouver, Canada",
  "America/Mexico_City": "Mexico City, Mexico",
  "America/Sao_Paulo": "São Paulo, Brazil",
  "America/Buenos_Aires": "Buenos Aires, Argentina",
  "Africa/Cairo": "Cairo, Egypt",
  "Africa/Johannesburg": "Johannesburg, South Africa",
  "Africa/Lagos": "Lagos, Nigeria",
}

function getTimezoneLocationLabel(timezone: string): string {
  if (TZ_TO_LOCATION[timezone]) return TZ_TO_LOCATION[timezone]
  const parts = timezone.split("/")
  const city = parts[parts.length - 1]?.replace(/_/g, " ") ?? timezone
  const region = parts[0] ?? ""
  return `${city} (${region})`
}

const FRANKFURTER_URL = "https://api.frankfurter.app/latest"
const CURRENCY_ROTATE_MS = 4000
const RATES_DELAYED_MS = 2000

export type RatesStatus = "live" | "delayed" | "error"

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: "$", EUR: "€", GBP: "£", JPY: "¥", CHF: "Fr", CAD: "C$", AUD: "A$",
  INR: "₹", CNY: "¥", BRL: "R$", MXN: "$", KRW: "₩", SGD: "S$", HKD: "HK$",
  NOK: "kr", SEK: "kr", DKK: "kr", NZD: "NZ$", ZAR: "R", RUB: "₽",
}

function formatRateValue(code: string, value: number): string {
  const sym = CURRENCY_SYMBOLS[code] ?? code + " "
  if (value >= 1000 || (value < 0.01 && value > 0)) return sym + value.toFixed(4)
  if (value >= 100) return sym + value.toFixed(2)
  if (value >= 1) return sym + value.toFixed(2)
  return sym + value.toFixed(4)
}

interface CurrencyRatesProps {
  pairs?: CurrencyPair[]
  onStatusChange?: (status: RatesStatus) => void
}

function pairKey(p: CurrencyPair) {
  return `${p.base}-${p.symbol}`
}

function CurrencyRates({ pairs: pairsProp, onStatusChange }: CurrencyRatesProps) {
  const stored = getStoredCurrencyPairs()
  const pairs = pairsProp?.length ? pairsProp : stored
  const [ratesByKey, setRatesByKey] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [index, setIndex] = useState(0)

  const pairsKey = useMemo(() => pairs.map((p) => pairKey(p)).sort().join(","), [pairs])
  const pair1 = useMemo(() => pairs.slice(0, 2), [pairs])
  const pair2 = useMemo(() => pairs.slice(2, 4), [pairs])

  useEffect(() => {
    const valid = pairs.filter((p) => p.base && p.symbol && p.base !== p.symbol)
    if (!valid.length) {
      setRatesByKey({})
      setLoading(false)
      onStatusChange?.("live")
      return
    }
    const start = Date.now()
    Promise.all(
      valid.map((p) =>
        fetch(`${FRANKFURTER_URL}?from=${p.base}&to=${p.symbol}`)
          .then((r) => (r.ok ? r.json() : Promise.reject(new Error("Fetch failed"))))
          .then((data: { rates?: Record<string, number> }) => ({ key: pairKey(p), rate: data.rates?.[p.symbol] }))
      )
    )
      .then((results) => {
        const next: Record<string, number> = {}
        results.forEach(({ key, rate }) => {
          if (rate != null) next[key] = rate
        })
        setRatesByKey(next)
        const elapsed = Date.now() - start
        onStatusChange?.(elapsed > RATES_DELAYED_MS ? "delayed" : "live")
      })
      .catch(() => {
        setRatesByKey({})
        onStatusChange?.("error")
      })
      .finally(() => setLoading(false))
  }, [pairsKey, onStatusChange])

  useEffect(() => {
    const pair2Len = Math.min(2, Math.max(0, pairs.length - 2))
    if (pair2Len === 0) return
    const t = setInterval(() => setIndex((i) => (i + 1) % 2), CURRENCY_ROTATE_MS)
    return () => clearInterval(t)
  }, [pairs.length])

  const renderRow = (rowPairs: CurrencyPair[]) => (
    <div className="space-y-1 text-xs tabular-nums">
      {rowPairs.map((p) => {
        const rate = ratesByKey[pairKey(p)]
        const pairLabel = `${p.symbol}/${p.base}`
        const valuePerUnit = rate != null && rate !== 0 ? 1 / rate : null
        const valueStr = valuePerUnit != null ? formatRateValue(p.base, valuePerUnit) : "—"
        return (
          <div key={pairKey(p)}>
            {pairLabel} {valueStr}
          </div>
        )
      })}
    </div>
  )

  if (loading && Object.keys(ratesByKey).length === 0) {
    return (
      <div className="text-xs text-muted-foreground space-y-1">
        <div className="h-4 w-4 rounded bg-muted animate-pulse" />
        <div className="h-4 w-4 rounded bg-muted animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-1.5 min-w-0">
      <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
        Exchange rates
      </div>
      <div className="relative h-10">
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{ opacity: index === 0 ? 1 : 0 }}
          aria-hidden={index !== 0}
        >
          {renderRow(pair1)}
        </div>
        <div
          className="absolute inset-0 transition-opacity duration-300"
          style={{ opacity: index === 1 ? 1 : 0 }}
          aria-hidden={index !== 1}
        >
          {renderRow(pair2)}
        </div>
      </div>
    </div>
  )
}

export interface WorldClockProps {
  /** API base URL for timezone lookup (used by map dialog) */
  apiBaseUrl?: string
  /** Optional class for the trigger button */
  className?: string
  /** Optional auth token getter for backend sync */
  getAuthToken?: () => string | null
}

export function WorldClock({ apiBaseUrl = DEFAULT_API_BASE, className, getAuthToken }: WorldClockProps) {
  const [locations, setLocations] = useState<ClockLocation[]>([])
  const [mapOpen, setMapOpen] = useState(false)
  const [currencyDialogOpen, setCurrencyDialogOpen] = useState(false)
  const [currencyPairs, setCurrencyPairs] = useState<CurrencyPair[]>([])
  const [ratesStatus, setRatesStatus] = useState<RatesStatus>("live")
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [now, setNow] = useState<Date | null>(null)

  // Move loadPrefs to a reusable function
  const loadPrefs = useCallback(async () => {
    const token = getAuthToken?.()
    if (!token) return

    try {
      const res = await fetch(`${apiBaseUrl}/api/account/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const data = await res.json()
        if (data.preferences) {
          const { worldClock, currencies } = data.preferences
          if (worldClock && Array.isArray(worldClock)) {
            setLocations(worldClock)
            setStoredClockLocations(worldClock)
          }
          if (currencies && Array.isArray(currencies)) {
            setCurrencyPairs(currencies)
            setStoredCurrencyPairs(currencies)
          }
        }
      }
    } catch (err) {
      console.debug("[WorldClock] Failed to load preferences:", err)
    }
  }, [apiBaseUrl, getAuthToken])

  // Load from storage initially
  useEffect(() => {
    const localClocks = getStoredClockLocations()
    const localCurrencies = getStoredCurrencyPairs()
    setLocations(localClocks)
    setCurrencyPairs(localCurrencies)
  }, [])

  // Refresh from backend when popover opens, just in case
  useEffect(() => {
    if (popoverOpen) {
      loadPrefs()
    }
  }, [popoverOpen, loadPrefs])

  useEffect(() => {
    setNow(new Date())
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const syncPreferences = useCallback(
    async (nextLocations?: ClockLocation[], nextCurrencies?: CurrencyPair[]) => {
      const token = getAuthToken?.()
      if (!token) return

      try {
        // Fetch current profile to get newest preferences before merging
        const res = await fetch(`${apiBaseUrl}/api/account/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!res.ok) return
        const data = await res.json()
        const currentPrefs = data.preferences || {}

        // Use backend as fallback to avoid deleting settings if local state is empty/stale
        const nextWorldClock = nextLocations || locations
        const finalWorldClock = nextWorldClock.length > 0 ? nextWorldClock : (currentPrefs.worldClock || [])

        const nextCurrs = nextCurrencies || currencyPairs
        const finalCurrs = nextCurrs.length > 0 ? nextCurrs : (currentPrefs.currencies || [])

        const nextPrefs = {
          ...currentPrefs,
          worldClock: finalWorldClock,
          currencies: finalCurrs,
        }

        await fetch(`${apiBaseUrl}/api/account/profile`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ preferences: nextPrefs })
        })
      } catch (err) {
        console.debug("[WorldClock] Failed to sync preferences:", err)
      }
    },
    [apiBaseUrl, getAuthToken, locations, currencyPairs]
  )

  useEffect(() => {
    const stored = getStoredClockLocations()
    const needsRefresh = stored.filter((loc) => loc.timezone === "UTC" && loc.lat != null && loc.lon != null)
    if (needsRefresh.length === 0) return
    let cancelled = false
    const run = async () => {
      const updated = [...stored]
      for (let i = 0; i < updated.length; i++) {
        if (cancelled) return
        const loc = updated[i]
        if (loc.timezone !== "UTC" || loc.lat == null || loc.lon == null) continue
        try {
          const res = await fetch(
            `${apiBaseUrl}/api/timezone?lat=${loc.lat}&lon=${loc.lon}`
          )
          if (res.ok) {
            const data = await res.json()
            const tz = data.timezone as string
            if (tz && tz !== "UTC") {
              updated[i] = { ...loc, timezone: tz }
            }
          }
        } catch {
          // keep UTC fallback
        }
      }
      if (!cancelled && JSON.stringify(updated) !== JSON.stringify(stored)) {
        setStoredClockLocations(updated)
        setLocations(updated)
        // Auto-sync the timezone resolution
        syncPreferences(updated)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [apiBaseUrl, syncPreferences])

  const { preferences } = useSidebarUser() || {}
  // const [locations, setLocations] = useState<ClockLocation[]>([]) // This line is already declared above, removing to avoid redeclaration

  // 1. Initial Load & Sync from Backend Preferences
  useEffect(() => {
    if (preferences?.worldClock?.locations) {
      setLocations(preferences.worldClock.locations)
      // Mirror to legacy storage for robustness
      setStoredClockLocations(preferences.worldClock.locations)
    } else {
      setLocations(getStoredClockLocations())
    }
    if (preferences?.worldClock?.currencyPairs) {
      setCurrencyPairs(preferences.worldClock.currencyPairs)
      setStoredCurrencyPairs(preferences.worldClock.currencyPairs)
    } else {
      setCurrencyPairs(getStoredCurrencyPairs())
    }
  }, [preferences?.worldClock?.locations, preferences?.worldClock?.currencyPairs])

  const handleSaveLocations = useCallback(async (next: ClockLocation[]) => {
    setLocations(next)
    setStoredClockLocations(next)
    
    // Sync to backend
    await updateUserPreferences({
      worldClock: {
        ...(preferences?.worldClock || {}),
        locations: next
      }
    })
  }, [preferences, updateUserPreferences])

  const handleSaveCurrencies = useCallback(async (next: CurrencyPair[]) => {
    setCurrencyPairs(next)
    setStoredCurrencyPairs(next)
    setCurrencyDialogOpen(false)
    
    // Sync to backend
    await updateUserPreferences({
      worldClock: {
        ...(preferences?.worldClock || {}),
        currencyPairs: next
      }
    })
  }, [preferences, updateUserPreferences])

  const handleRatesStatusChange = useCallback((status: RatesStatus) => {
    setRatesStatus(status)
  }, [])

  const localTz = getLocalTimezone()
  const localTime = formatTime(localTz)
  const localShort = formatTimezoneShort(localTz, undefined)
  const localLocation = getTimezoneLocationLabel(localTz)

  return (
    <TooltipProvider>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-9 w-9 rounded-full", className)}
                aria-label="World clock"
              >
                <Clock className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="bottom">World clock</TooltipContent>
        </Tooltip>
        <PopoverContent
          className="w-[min(320px,90vw)] p-0 border bg-popover text-popover-foreground shadow-md mt-3 relative"
          align="end"
          side="bottom"
          sideOffset={12}
        >
          <div
            className={cn(
              "absolute top-2 right-2 h-2 w-2 rounded-full shrink-0 z-10",
              ratesStatus === "live" && "status-dot-live",
              ratesStatus === "delayed" && "status-dot-delayed",
              ratesStatus === "error" && "status-dot-error",
            )}
            title={ratesStatus === "live" ? "Live" : ratesStatus === "delayed" ? "Delayed" : "Error"}
            aria-label={ratesStatus === "live" ? "Live" : ratesStatus === "delayed" ? "Delayed" : "Error"}
          />
          <div className="p-3 border-b bg-muted/30 grid grid-cols-[minmax(0,1fr)_1px_minmax(0,1fr)] gap-0">
            <div className="min-w-0 pr-3">
              <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
                Local time
              </div>
              <div className="text-lg font-semibold tabular-nums">{localTime}</div>
              <div className="text-xs text-muted-foreground truncate" title={`${localShort} – ${localLocation}`}>
                {localShort} – {localLocation}
              </div>
            </div>
            <div className="w-px bg-border self-stretch min-h-[3rem]" aria-hidden />
            <div className="min-w-0 pl-3">
              <CurrencyRates
                pairs={currencyPairs}
                onStatusChange={handleRatesStatusChange}
              />
            </div>
          </div>
          <div className="p-3 max-h-[200px] overflow-auto">
            {locations.length === 0 ? (
              <p className="text-sm text-muted-foreground">Add locations via the map.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {locations.map((loc) => (
                  <div
                    key={loc.id}
                    className="flex flex-col rounded-md border bg-background/50 px-3 py-2 text-sm min-w-0"
                  >
                    <div className="text-lg font-semibold tabular-nums" suppressHydrationWarning>
                      {now ? formatTime(loc.timezone, loc.lon) : "--:--"}
                    </div>
                    <div className="text-xs text-muted-foreground truncate" suppressHydrationWarning title={`${formatTimezoneShort(loc.timezone, loc.lon)} - ${loc.label}`}>
                      {formatTimezoneShort(loc.timezone, loc.lon)} – {loc.label}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="p-2 border-t">
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setMapOpen(true)}
            >
              <MapPin className="h-4 w-4 mr-2" />
              Select locations on map
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full mt-2"
              onClick={() => setCurrencyDialogOpen(true)}
            >
              <Settings2 className="h-4 w-4 mr-2" />
              Select currencies
            </Button>
            <TimezoneMapDialog
              open={mapOpen}
              onOpenChange={setMapOpen}
              locations={locations}
              onSave={handleSaveLocations}
              apiBaseUrl={apiBaseUrl}
            />
            <CurrencySelectDialog
              open={currencyDialogOpen}
              onOpenChange={setCurrencyDialogOpen}
              onSave={handleSaveCurrencies}
            />
          </div>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  )
}
